import { NgOptimizedImage } from '@angular/common';
import {
	Component,
	computed,
	DestroyRef,
	inject,
	input,
	signal,
	ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { JeptoHelpArticleComponent } from '@framework/ui-lib/help/jepto-help-article/jepto-help-article.component';
import { SharedModule } from '@jepto/shared/shared.module';
import { JepDrawerService } from '@jepto/shared/services/jep-drawer/jep-drawer.service';

import {
	AI_CREDITS_USAGE_HELP_DRAWER_ID,
	AI_CREDITS_USAGE_HELP_TITLE,
	buildAiCreditsUsageHelpContent,
	buildAiCreditsUsageRelatedArticles,
} from '../../constants/ai-credits-usage-help';

import type { AiCreditsIncentiveItem } from '../../models/ai-credits-incentive.model';
import { AiCreditsIncentiveStore } from '../../stores/ai-credits-incentive.store';
import { openAiCreditsIncentiveModal } from '../jepto-ai-credits-incentive-modal/jepto-ai-credits-incentive-modal.dialog';

interface IncentiveRow {
	readonly item: AiCreditsIncentiveItem;
	readonly actionable: boolean;
	readonly completed: boolean;
	readonly pending: boolean;
	readonly reward: string;
}

@Component({
	selector: 'jepto-ai-credits-incentives-panel',
	imports: [NgOptimizedImage, SharedModule, JeptoHelpArticleComponent],
	templateUrl: './jepto-ai-credits-incentives-panel.component.html',
	styleUrl: './jepto-ai-credits-incentives-panel.component.scss',
	encapsulation: ViewEncapsulation.None,
	host: {
		class: 'jepto-ai-credits-incentives-panel',
		'[class.jepto-ai-credits-incentives-panel--expanded]': 'isExpanded()',
		'[class.jepto-ai-credits-incentives-panel--topbar]':
			'variant() === "topbar"',
	},
})
export class JeptoAiCreditsIncentivesPanelComponent {
	readonly testId = input<string | undefined>();
	readonly variant = input<'sidebar' | 'topbar'>('sidebar');

	private readonly incentiveStore = inject(AiCreditsIncentiveStore);
	private readonly matDialog = inject(MatDialog);
	private readonly jepDrawerService = inject(JepDrawerService);
	private readonly destroyRef = inject(DestroyRef);

	private readonly _expanded = signal(false);

	readonly incentives = this.incentiveStore.incentives;
	readonly isLoading = this.incentiveStore.isLoading;
	readonly isExpanded = this._expanded.asReadonly();

	readonly helpTitle = AI_CREDITS_USAGE_HELP_TITLE;
	readonly helpDrawerId = AI_CREDITS_USAGE_HELP_DRAWER_ID;
	readonly helpContent = buildAiCreditsUsageHelpContent();
	readonly relatedArticles = buildAiCreditsUsageRelatedArticles();

	readonly resolvedTestId = computed(
		() => this.testId() ?? 'jepto-ai-credits-incentives-panel'
	);

	readonly incentiveRows = computed((): IncentiveRow[] =>
		this.incentives().map(item => ({
			item,
			actionable: this.isActionable(item),
			completed: this.isCompleted(item),
			pending: this.isPending(item),
			reward: this.rewardLabel(item),
		}))
	);

	readonly availableCreditsSummary = computed(() => {
		const availableCredits = this.incentives()
			.filter(item => this.isActionable(item))
			.reduce((total, item) => total + item.monthlyCredits, 0);

		if (availableCredits <= 0) {
			return 'All claimed';
		}

		return `Earn ${availableCredits.toLocaleString()} every month`;
	});

	constructor() {
		void this.incentiveStore.loadIncentives();
		this.destroyRef.onDestroy(() => this.closeAiCreditsHelp());
	}

	toggleExpanded(): void {
		this._expanded.update(expanded => !expanded);
	}

	onDismiss(event: Event): void {
		event.preventDefault();
		event.stopPropagation();
		void this.incentiveStore.dismissIncentivesPanel();
	}

	/**
	 * A rejected task can be attempted again, so it counts towards the credits
	 * still on offer and stays clickable — only verified and pending are closed.
	 */
	isActionable(item: AiCreditsIncentiveItem): boolean {
		return item.status === 'available' || item.status === 'rejected';
	}

	onIncentiveClick(item: AiCreditsIncentiveItem, event?: Event): void {
		event?.preventDefault();
		event?.stopPropagation();

		if (!this.isActionable(item)) {
			return;
		}

		// Collapse before the modal opens so the topbar dropdown cannot
		// sit over the dialog on narrow viewports.
		this._expanded.set(false);
		openAiCreditsIncentiveModal(
			this.matDialog,
			item,
			this.incentiveStore
		);
	}

	isCompleted(item: AiCreditsIncentiveItem): boolean {
		return item.status === 'verified';
	}

	isPending(item: AiCreditsIncentiveItem): boolean {
		return item.status === 'pending';
	}

	/** Available tasks show the monthly reward; closed tasks show their status. */
	rewardLabel(item: AiCreditsIncentiveItem): string {
		if (item.status === 'verified') {
			return 'Earning';
		}
		if (item.status === 'pending') {
			return 'Checking';
		}
		return `+${item.monthlyCredits}/mo`;
	}

	openAiCreditsHelp(event: Event): void {
		event.preventDefault();
		event.stopPropagation();
		this._expanded.set(false);
		this.jepDrawerService.open(this.helpDrawerId);
	}

	closeAiCreditsHelp(): void {
		this.jepDrawerService.close(this.helpDrawerId);
	}
}
